import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Trash2, Landmark, TrendingUp, TrendingDown, Scale, HelpCircle, Edit2, Check, X, Repeat } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { puoFare } from '../../utils/permessi';

const fmt = (n) => `€ ${Math.abs(n || 0).toFixed(2)}`;
const oggiISO = () => new Date().toISOString().slice(0, 10);
const primoGennaioISO = () => `${new Date().getFullYear()}-01-01`;

export default function Centri() {
  const { user } = useAuth();
  const puoCreare    = puoFare(user, 'cassa', 'create');
  const puoModificare = puoFare(user, 'cassa', 'edit');
  const puoEliminare  = puoFare(user, 'cassa', 'delete');
  const [centri, setCentri] = useState([]);
  const [report, setReport] = useState(null);
  const [rinomina, setRinomina] = useState(null); // { id, nome }
  const [da, setDa] = useState(primoGennaioISO());
  const [a, setA] = useState(oggiISO());
  const [showNew, setShowNew] = useState(false);
  const [nuovoNome, setNuovoNome] = useState('');
  const [msg, setMsg] = useState('');

  const toast = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3000); };

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (da) params.set('da', da);
    if (a) params.set('a', a);
    const [c, r] = await Promise.all([
      api.get('/centri'),
      api.get(`/centri/report?${params.toString()}`),
    ]);
    setCentri(c.data);
    setReport(r.data);
  }, [da, a]);

  useEffect(() => { load(); }, [load]);

  const creaCentro = async (e) => {
    e.preventDefault();
    if (!nuovoNome.trim()) return;
    try {
      await api.post('/centri', { nome: nuovoNome.trim() });
      setNuovoNome(''); setShowNew(false); load(); toast('Centro creato');
    } catch (err) { toast('✗ ' + (err.response?.data?.error || 'Errore')); }
  };

  const eliminaCentro = async (id) => {
    if (!window.confirm('Eliminare questo centro? Le sue categorie resteranno "senza centro" finché non le assegni a un altro.')) return;
    await api.delete(`/centri/${id}`);
    load();
  };

  const salvaRinomina = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/centri/${rinomina.id}`, { nome: rinomina.nome });
      setRinomina(null); load();
    } catch (err) { toast('✗ ' + (err.response?.data?.error || 'Errore')); }
  };

  const preset = (giorni) => {
    if (giorni === 'anno') { setDa(primoGennaioISO()); setA(oggiISO()); }
    else if (giorni === 'mese') { const d = new Date(); setDa(new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0,10)); setA(oggiISO()); }
    else if (giorni === 'tutto') { setDa(''); setA(''); }
  };

  const rigaSpeciale = (titolo, sotto, r, colore) => r && (
    <div style={{ ...s.card, borderStyle: 'dashed', opacity: 0.85 }}>
      <div style={s.cardIcon}><HelpCircle size={16} color={colore} /></div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: '700', fontSize: 'var(--font-size-base)' }}>{titolo}</div>
        <div style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-xs)' }}>{r.n_movimenti} movimenti — {sotto}</div>
      </div>
      <div style={s.importoCol}><span style={s.credLabel}>Entrate</span><span style={{ color: '#22C55E', fontWeight: '700' }}>{fmt(r.entrate)}</span></div>
      <div style={s.importoCol}><span style={s.credLabel}>Uscite</span><span style={{ color: '#EF4444', fontWeight: '700' }}>{fmt(r.uscite)}</span></div>
      <div style={s.importoCol}><span style={s.credLabel}>Saldo</span><span style={{ fontWeight: '800' }}>{r.saldo >= 0 ? '+' : '-'}{fmt(r.saldo)}</span></div>
    </div>
  );

  return (
    <div>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>CENTRI DI COSTO/RICAVO</h1>
          <p style={s.sub}>Quanto è entrato e uscito per ogni area nel periodo scelto. Ogni movimento finisce nel centro della sua categoria: il centro di ogni categoria si sceglie in <Link to="/cash/banca" style={{ color: '#F4600C' }}>Movimenti bancari → Categorie</Link>.</p>
        </div>
        {puoCreare && <button style={s.btnPri} onClick={() => setShowNew(true)}><Plus size={14} /> NUOVO CENTRO</button>}
      </div>

      {msg && <div style={s.toast}>{msg}</div>}

      <div style={s.filterBar}>
        <button style={s.presetBtn} onClick={() => preset('mese')}>QUESTO MESE</button>
        <button style={s.presetBtn} onClick={() => preset('anno')}>QUEST'ANNO</button>
        <button style={s.presetBtn} onClick={() => preset('tutto')}>SEMPRE</button>
        <div style={{ width: '1px', background: 'var(--border2)', margin: '0 6px' }} />
        <label style={s.label}>DAL</label>
        <input type="date" style={s.dateInput} value={da} onChange={e => setDa(e.target.value)} />
        <label style={s.label}>AL</label>
        <input type="date" style={s.dateInput} value={a} onChange={e => setA(e.target.value)} />
      </div>

      {showNew && (
        <form onSubmit={creaCentro} style={{ ...s.formCard, display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <label style={s.label}>NOME CENTRO</label>
            <input style={s.input} autoFocus value={nuovoNome} onChange={e => setNuovoNome(e.target.value)} placeholder="es. Corsi Q1, Torneo Estivo, Merch, Affitto palestra..." />
          </div>
          <button type="button" style={s.btnSec} onClick={() => setShowNew(false)}>ANNULLA</button>
          <button type="submit" style={s.btnPri}>CREA</button>
        </form>
      )}

      {report && (
        <>
          <div style={s.totaliRow}>
            <div style={s.totaleCard}>
              <TrendingUp size={16} color="#22C55E" />
              <div><div style={s.totaleLabel}>ENTRATE TOTALI</div><div style={{ ...s.totaleValore, color: '#22C55E' }}>{fmt(report.totale.entrate)}</div></div>
            </div>
            <div style={s.totaleCard}>
              <TrendingDown size={16} color="#EF4444" />
              <div><div style={s.totaleLabel}>USCITE TOTALI</div><div style={{ ...s.totaleValore, color: '#EF4444' }}>{fmt(report.totale.uscite)}</div></div>
            </div>
            <div style={s.totaleCard}>
              <Scale size={16} color={report.totale.saldo >= 0 ? '#22C55E' : '#EF4444'} />
              <div><div style={s.totaleLabel}>SALDO</div><div style={{ ...s.totaleValore, color: report.totale.saldo >= 0 ? '#22C55E' : '#EF4444' }}>{report.totale.saldo >= 0 ? '+' : '-'}{fmt(report.totale.saldo)}</div></div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '24px' }}>
            {report.centri.length === 0 && (
              <div style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-sm)', padding: '20px' }}>Nessun centro creato. Creane uno per iniziare a suddividere entrate e uscite.</div>
            )}
            {report.centri.map(c => (
              <div key={c.id} className="riga-scheda" style={s.card}>
                <div style={s.cardIcon}><Landmark size={16} color="#F4600C" /></div>
                <div className="riga-info" style={{ flex: 1, minWidth: 0 }}>
                  {rinomina?.id === c.id ? (
                    <form onSubmit={salvaRinomina} style={{ display: 'flex', gap: '6px', marginBottom: '4px' }}>
                      <input style={{ ...s.input, padding: '5px 8px' }} autoFocus value={rinomina.nome} onChange={e => setRinomina(p => ({ ...p, nome: e.target.value }))} />
                      <button type="submit" style={s.iconBtn} title="Salva"><Check size={13} /></button>
                      <button type="button" style={s.iconBtn} title="Annulla" onClick={() => setRinomina(null)}><X size={13} /></button>
                    </form>
                  ) : (
                    <div style={{ fontWeight: '700', fontSize: 'var(--font-size-base)', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {c.nome}
                      {puoModificare && <Edit2 size={11} style={{ cursor: 'pointer', opacity: 0.5 }} onClick={() => setRinomina({ id: c.id, nome: c.nome })} />}
                    </div>
                  )}
                  <div style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-xs)' }}>
                    {c.n_movimenti} movimenti nel periodo · {c.categorie.length ? c.categorie.map(k => k.nome).join(', ') : <span style={{ color: '#F59E0B' }}>nessuna categoria collegata</span>}
                  </div>
                </div>
                <div style={s.importoCol}><span style={s.credLabel}>Entrate</span><span style={{ color: '#22C55E', fontWeight: '700' }}>{fmt(c.entrate)}</span></div>
                <div style={s.importoCol}><span style={s.credLabel}>Uscite</span><span style={{ color: '#EF4444', fontWeight: '700' }}>{fmt(c.uscite)}</span></div>
                <div style={s.importoCol}><span style={s.credLabel}>Saldo</span><span style={{ color: c.saldo >= 0 ? '#22C55E' : '#EF4444', fontWeight: '800' }}>{c.saldo >= 0 ? '+' : '-'}{fmt(c.saldo)}</span></div>
                {puoEliminare && <button style={{ ...s.iconBtn, color: '#EF4444' }} onClick={() => eliminaCentro(c.id)}><Trash2 size={13} /></button>}
              </div>
            ))}

            {rigaSpeciale('Categorie senza centro', report.categorieSenzaCentro.length ? `categorizzati ma la categoria non ha un centro: ${report.categorieSenzaCentro.map(k => k.nome).join(', ')}` : 'categorizzati ma la categoria non ha un centro', report.senzaCentro, '#F59E0B')}
            {rigaSpeciale('Movimenti non categorizzati', 'ancora senza categoria, da sistemare in Movimenti bancari', report.nonCategorizzati, '#EF4444')}
            {report.giroconti.n > 0 && (
              <div style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-xs)', display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 4px' }}>
                <Repeat size={12} /> {report.giroconti.n} giroconti esclusi dai totali (spostamenti tra conti propri: {fmt(report.giroconti.entrate)} entrati, {fmt(report.giroconti.uscite)} usciti)
              </div>
            )}
            {report.categorieSenzaCentro.length > 0 && !report.senzaCentro && (
              <div style={{ color: '#F59E0B', fontSize: 'var(--font-size-xs)', padding: '4px 4px' }}>
                Categorie senza centro (ancora senza movimenti nel periodo): {report.categorieSenzaCentro.map(k => k.nome).join(', ')}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const s = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' },
  title: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'var(--font-size-title)', fontWeight: '800', letterSpacing: '2px' },
  sub: { color: 'var(--text-dim)', fontSize: 'var(--font-size-sm)', marginTop: '4px', maxWidth: '520px' },
  btnPri: { display: 'flex', alignItems: 'center', gap: '8px', background: '#F4600C', color:'#fff', border: 'none', borderRadius: '8px', padding: '10px 16px', fontSize: 'var(--font-size-xs)', fontWeight: '700', cursor: 'pointer' },
  btnSec: { background: 'var(--border)', color: 'var(--text)', border: '1px solid var(--border2)', borderRadius: '8px', padding: '8px 12px', fontSize: 'var(--font-size-xs)', fontWeight: '700', cursor: 'pointer' },
  filterBar: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' },
  presetBtn: { background: 'var(--bg-input)', border: '1px solid var(--border2)', borderRadius: '6px', padding: '7px 12px', color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)', fontWeight: '700', cursor: 'pointer' },
  dateInput: { background: 'var(--bg-input)', border: '1px solid var(--border2)', borderRadius: '6px', padding: '6px 10px', color: 'var(--text)', fontSize: 'var(--font-size-xs)', outline: 'none' },
  label: { fontSize: 'var(--font-size-xs)', letterSpacing: '1px', fontWeight: '700', color: 'var(--text-dim)' },
  formCard: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px', marginBottom: '20px' },
  formTitle: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'var(--font-size-sm)', fontWeight: '700', letterSpacing: '2px', marginBottom: '12px', color: 'var(--text-dim)' },
  input: { background: 'var(--bg-input)', border: '1px solid var(--border2)', borderRadius: '8px', padding: '9px 12px', color: 'var(--text)', fontSize: 'var(--font-size-sm)', outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' },
  totaliRow: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '20px' },
  totaleCard: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '12px' },
  totaleLabel: { fontSize: 'var(--font-size-xs)', letterSpacing: '1px', color: 'var(--text-dim)', fontWeight: '700' },
  totaleValore: { fontSize: 'var(--font-size-lg)', fontWeight: '800' },
  card: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '16px' },
  cardIcon: { width: '36px', height: '36px', background: '#F4600C15', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  importoCol: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: '90px', gap: '2px', fontSize: 'var(--font-size-sm)' },
  credLabel: { color: 'var(--text-dim)', fontSize: 'var(--font-size-xs)' },
  iconBtn: { background: 'var(--bg-input)', border: '1px solid var(--border2)', borderRadius: '6px', padding: '7px 9px', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center' },
  toast: { background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: '8px', padding: '10px 16px', marginBottom: '16px', fontSize: 'var(--font-size-sm)' },
};
